import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import {
  useGameSessionError,
  useGameSessionStore
} from '@/stores/gameSessionStore';
import SessionCreationWizard from './SessionCreationWizard';
import AiGameCreationModal from './AiGameCreationModal';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';

interface GameControlProps {
  onCreateSession?: () => void;
  onAiGameCreated?: () => void;
}

const GameControl = ({ onAiGameCreated }: GameControlProps) => {
  const navigate = useSafeNavigate();
  const { user, loadQuestionSets } = useAuthStore();

  // Zustand store hooks
  const error = useGameSessionError();

  // Get individual action functions directly from the store
  const loadSessions = useGameSessionStore(state => state.loadSessions);
  const clearError = useGameSessionStore(state => state.clearError);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAiGameModal, setShowAiGameModal] = useState(false);
  const [createdAiSession, setCreatedAiSession] = useState<import('@/types').AiGameSession | null>(null);

  // Load question sets on mount
  useEffect(() => {
    console.log('🎮 GameControl: Component mounted, user:', user?.uid);
    if (user?.uid) {
      console.log('🎮 GameControl: Loading sessions and question sets...');
      loadSessions(user.uid);
      loadQuestionSets();
    }
  }, [user?.uid, loadSessions, loadQuestionSets]);

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(20px)',
      borderRadius: '20px',
      padding: '2rem',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '2rem'
      }}>
        <h2 style={{
          color: '#374151',
          margin: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          🎮 Game Control
        </h2>
        <p style={{ color: '#64748b', margin: '0.5rem 0 0 0' }}>
          Create and launch game sessions
        </p>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '1rem',
          color: '#dc2626'
        }}>
          {error}
        </div>
      )}

      {/* Main Content */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        minHeight: '400px'
      }}>

        {/* Create Session Card */}
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#64748b'
        }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎯</div>
          <h3 style={{ color: '#374151', marginBottom: '0.5rem', fontSize: '1.5rem' }}>Ready to Start a Game?</h3>
          <p style={{ marginBottom: '2rem', fontSize: '1rem' }}>Create a new session and your students can join instantly!</p>
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '1rem 2rem',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }}
            >
              🚀 Create Multiplayer Game (live)
            </button>
            <button
              onClick={() => setShowAiGameModal(true)}
              style={{
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '1rem 2rem',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
              }}
            >
              🤖 Create Game for AI Play
            </button>
          </div>
        </div>

      </div>

      {/* Session Creation Wizard */}
      {showCreateForm && (
        <SessionCreationWizard
          onSessionCreated={(sessionId, gameCode) => {
            setShowCreateForm(false);
            clearError();
            console.log('✅ Session created successfully:', { sessionId, gameCode });
            // Navigate directly to the game view
            navigate(`/teacher/game/${sessionId}`);
          }}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* AI Game Creation Modal */}
      {showAiGameModal && !createdAiSession && (
        <AiGameCreationModal
          onGameStarted={(session) => {
            setShowAiGameModal(false);
            setCreatedAiSession(session);
            clearError();
            console.log('✅ AI game created successfully:', session);
            // Trigger refresh of ongoing games list
            onAiGameCreated?.();
          }}
          onCancel={() => setShowAiGameModal(false)}
        />
      )}

      {/* AI Game Success Modal - Display Game Code */}
      {createdAiSession && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001,
          padding: '1rem'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '3rem 2rem',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🎉</div>
            <h2 style={{
              color: '#374151',
              marginBottom: '1rem',
              fontSize: '1.8rem'
            }}>
              AI Game Created!
            </h2>
            <p style={{
              color: '#64748b',
              marginBottom: '2rem',
              fontSize: '1rem'
            }}>
              Share this code with your students to let them play
            </p>

            {/* Game Code Display */}
            <div style={{
              background: 'linear-gradient(135deg, #f97316, #ea580c)',
              borderRadius: '16px',
              padding: '2rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '0.9rem',
                marginBottom: '0.5rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                Game Code
              </div>
              <div style={{
                color: 'white',
                fontSize: '3.5rem',
                fontWeight: 'bold',
                letterSpacing: '0.5rem',
                fontFamily: 'monospace'
              }}>
                {createdAiSession.gameCode}
              </div>
            </div>

            {/* Game Details */}
            <div style={{
              background: '#f9fafb',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '2rem',
              textAlign: 'left'
            }}>
              <div style={{
                display: 'grid',
                gap: '1rem'
              }}>
                <div>
                  <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Question Set
                  </div>
                  <div style={{ color: '#374151', fontWeight: '600' }}>
                    {createdAiSession.questionSetName}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    AI Configuration
                  </div>
                  <div style={{ color: '#374151', fontWeight: '600' }}>
                    {createdAiSession.allowStudentChoice
                      ? '👥 Students choose AI count'
                      : `🤖 ${createdAiSession.fixedAiPlayerCount} AI opponents (fixed)`}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                    Available Until
                  </div>
                  <div style={{ color: '#374151', fontWeight: '600' }}>
                    {new Date(createdAiSession.endDate).toLocaleString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(createdAiSession.gameCode);
                  alert('Game code copied to clipboard!');
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #f97316, #ea580c)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(249, 115, 22, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(249, 115, 22, 0.3)';
                }}
              >
                📋 Copy Code
              </button>
              <button
                onClick={() => setCreatedAiSession(null)}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameControl;
